import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { useNumberFormat } from '../../lib/numberFormat';

/** Drill-through: shows the calculation, upstream rows and assumptions for a clicked line. */
export function DrillDrawer({ row, rows, assumptions, schema, values, labels, currency, onClose, onJump }) {
  const { fmt } = useNumberFormat();
  if (!row) return null;
  const byKey = Object.fromEntries(rows.map((r) => [r.key, r]));
  const readable = (row.expr || '')
    .replace(/prev\((\w+)(?:,\s*(\d+))?\)/g, (_, k, n) => `${byKey[k]?.label || k} [${n ? `${n} months earlier` : 'previous month'}]`)
    .replace(/\ba\.(\w+)/g, (_, k) => `«${schema.find((a) => a.key === k)?.label || k}»`)
    .replace(/\bob\.(\w+)/g, (_, k) => `Opening ${k.replace(/_/g, ' ')}`)
    .replace(/\bhc\.r(\d+)_(\w+)/g, (_, i, f) => `Role ${i} ${f}`)
    .replace(/\b([a-z_][a-z0-9_]*)\b/g, (m) => (byKey[m] ? byKey[m].label : m))
    .replace(/\*\*/g, '^').replace(/==/g, '=');
  return <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="drill-drawer">
      <SheetHeader>
        <SheetTitle className="font-heading">{row.label}</SheetTitle>
        <SheetDescription>{row.help || 'How this line is calculated and what drives it.'}</SheetDescription>
      </SheetHeader>
      <div className="mt-6 space-y-6 text-sm">
        <div>
          <p className="eyebrow mb-2">Calculation</p>
          <p className="rounded-md border border-border bg-secondary p-3 leading-relaxed" data-testid="drill-formula">{readable}</p>
          <p className="help">Sheet in Excel: <span className="font-medium text-foreground">{row.sheet}</span>. The same expression is written as a live formula in the workbook.</p>
        </div>
        {row.assumptions?.length > 0 && <div>
          <p className="eyebrow mb-2">Assumptions used</p>
          <ul className="divide-y divide-border rounded-md border border-border">
            {row.assumptions.map((a) => { const k = a.split('.')[1]; const s = schema.find((x) => x.key === k);
              return <li key={a} className="flex items-center justify-between gap-3 px-3 py-2"><span>{s?.label || a}</span><span className="tabular font-medium">{a.startsWith('a.') ? fmt(assumptions[k], s?.unit, currency) : '—'}</span></li>; })}
          </ul>
        </div>}
        {row.depends_on?.length > 0 && <div>
          <p className="eyebrow mb-2">Upstream lines</p>
          <ul className="space-y-1">
            {row.depends_on.filter((d) => byKey[d]).map((d) => <li key={d}><button className="text-left text-[hsl(var(--teal))] underline-offset-2 hover:underline" onClick={() => onJump(byKey[d])} data-testid={`drill-jump-${d}`}>{byKey[d].label}</button> <span className="text-xs text-muted-foreground">({byKey[d].sheet})</span></li>)}
          </ul>
        </div>}
        <div>
          <p className="eyebrow mb-2">Values</p>
          <div className="max-h-56 overflow-auto rounded-md border border-border">
            <table className="w-full text-xs"><tbody>
              {labels.map((l, i) => <tr key={l} className="border-b border-border last:border-0"><td className="px-3 py-1 text-muted-foreground">{l}</td><td className={`tabular px-3 py-1 text-right ${values[row.key]?.[i] < 0 ? 'neg' : ''}`}>{fmt(values[row.key]?.[i], row.unit, currency)}</td></tr>)}
            </tbody></table>
          </div>
        </div>
      </div>
    </SheetContent>
  </Sheet>;
}
